import Head from "next/head";

export type ArticleProps = {
  title: string;
  description: string;
  imageUrl: string;
  publishDate: string;
  author: string;
  authorSlug: string;
  slug: string;
  category: string;
  tags: string[];
  sport: string;
  modified: string;
};

// Available sports in the system
const AVAILABLE_SPORTS = ["NRL", "AFL", "BBL", "EPL", "NBA", "NFL"] as const;

/**
 * Extracts the sport from an array of category names
 * @param categories Array of category names
 * @returns The sport name if found, otherwise 'BBL' as default
 */
export const getSportFromCategories = (categories: string[]): string => {
  if (!categories || categories.length === 0) {
    return "NRL"; // Default sport
  }

  // Convert categories to uppercase for case-insensitive matching
  const upperCategories = categories.map((cat) => cat.toUpperCase());

  // Find the first matching sport
  for (const sport of AVAILABLE_SPORTS) {
    if (upperCategories.includes(sport)) {
      return sport;
    }
  }

  // Default to NRL if no sport category found
  return "NRL";
};

const generateArticleJsonLd = (article: ArticleProps) => {
  let tags = article.tags ?? [];
  const tagsString = [
    ...tags,
    "supercoach",
    "supercoach news",
    "supercoach stats",
    article.sport,
    `${article.sport} supercoach`,
    `${article.sport} supercoach stats`,
    `${article.sport} supercoach news`,
  ].join(", ");
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: article.description,
    image: article.imageUrl,
    datePublished: article.publishDate,
    dateModified: article.modified,
    author: {
      "@type": "Person",
      name: article.author,
      url: `https://scplaybook.com.au/contributors/${article.authorSlug}`,
    },
    publisher: {
      "@type": "Organization",
      name: "Supercoach Playbook",
      logo: {
        "@type": "ImageObject",
        url: "https://scplaybook.com.au/_next/image?url=%2Fimages%2Flogos%2FLogo-Blue-White.svg&w=3840&q=75",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://scplaybook.com.au/blog${article.slug}`,
    },
    articleSection: article.category,
    keywords: tagsString,
    sport: article.sport,
    coverage: "pre game", // e.g., "live", "post-game"
  };
};

const ArticleHead = ({ article }: { article: ArticleProps }) => {
  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateArticleJsonLd(article)),
        }}
      />
    </Head>
  );
};

export default ArticleHead;
